'use client';

import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';
import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';

export type BottomBarProps = {
  className?: {
    root?: string;
  };
  items?: BottomBarItemProps[];
};

export function BottomBar({ items = HEADER_CONFIG.bottomBar.items, ...props }: BottomBarProps) {
  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 top-auto z-50 grid h-[52px] grid-cols-5 bg-background px-4 pt-2 shadow-[0px_0px_15px_rgba(0,0,0,0.1)] sm:hidden',
        'ea-bottom-bar',
        props.className?.root,
      )}
    >
      {items?.map(({ key, ...item }) => (
        <BottomBarItem key={key} {...item} />
      ))}
    </div>
  );
}

export type BottomBarItemProps = {
  key: string;
  inactiveIcon?: string;
  activeIcon?: string;
  label?: string;
  className?: {
    root?: string;
    iconContainer?: string;
    label?: string;
    active?: string;
  };
  href: string | (() => string);
};
export function BottomBarItem(props: BottomBarItemProps) {
  if (!props.inactiveIcon && !props.activeIcon && !props.label) {
    throw new Error('Either icon or label must be provided');
  }
  const pathname = usePathname();
  let href = typeof props.href === 'function' ? props.href() : props.href;
  const isActive = arePathsEqual(pathname, href);
  const me = useCustomerStore(s => s.me);
  const isCustomerStoreInitialized = useCustomerStore(s => s.isInitialized);

  if (
    !me &&
    isCustomerStoreInitialized &&
    (href === ROUTES.PROFILE() || href === ROUTES.FAVORITES())
  ) {
    href = ROUTES.SIGN_IN();
  }

  const { t } = useTranslations();

  return (
    <Link
      href={href ?? '#'}
      className={cn(
        'col-span-1 flex w-full flex-col items-center justify-center gap-1 rounded-[4px]',
        'ea-bottom-bar__item text-black',
        props.className?.root,
        {
          'ea-bottom-bar__item--active bg-primary-foreground text-primary': isActive,
        },
      )}
      prefetch={false}
    >
      {props.activeIcon && props.inactiveIcon && (
        <Image
          src={isActive ? props.activeIcon : props.inactiveIcon}
          // Decorative: the translated label is rendered right below. `props.label` is the raw
          // translation key ("text160"), which screen readers used to announce.
          alt=""
          width={24}
          height={24}
          className="shrink-0 object-contain"
        />
      )}
      {props.label && (
        <div
          className={cn(
            'text-center text-[10px] font-normal leading-[100%]',
            'ea-bottom-bar__item__label',
            props.className?.label,
          )}
        >
          {t(props.label)}
        </div>
      )}
    </Link>
  );
}
